import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Coins, ArrowRight, Scale, ShieldCheck, HandCoins, ClipboardCheck,
  Lock, MessageCircleQuestion, Info, MapPin,
} from "lucide-react";
import { STATES, CHARGE_TYPES } from "@/data/legalData";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SEVERITY = {
  "Traffic / other misdemeanor": "low",
  "DUI / DWI": "low-med",
  "Drug possession": "low-med",
  "Theft / larceny": "med",
  "Fraud / white-collar": "med",
  "Assault / battery": "med-high",
  "Weapons charge": "med-high",
  "Domestic violence": "high",
  "Federal charge": "high",
  "Probation / parole violation": "high",
  "Other / not sure": "unknown",
};

const PRIMARY = {
  low: "Release on your own recognizance (ROR) or a low bail is common for charges like this.",
  "low-med": "ROR or a moderate bail is common — especially for a first offense with strong community ties.",
  med: "Expect bail to be set. The amount usually depends on your record and your ties to the community.",
  "med-high": "Bail is likely and may be significant, often with conditions of release attached.",
  high: "Higher bail, strict release conditions, or (in serious cases) a detention hearing are possible.",
  unknown: "Outcomes vary widely — bail depends heavily on the exact charge and your history.",
};

const chancePill = (label) => {
  const map = {
    Likely: "bg-[#ECFDF5] text-[#047857]",
    Possible: "bg-forest-100 text-forest-800",
    "Common option": "bg-forest-100 text-forest-800",
    "Less likely": "bg-[#FFFBEB] text-[#8a5a12]",
    Unlikely: "bg-[#FFFBEB] text-[#8a5a12]",
    Rare: "bg-parchment-200 text-ink-500",
    Depends: "bg-parchment-200 text-ink-500",
  };
  return map[label] || "bg-parchment-200 text-ink-500";
};

function buildBail(chargeType, stateObj) {
  const sev = SEVERITY[chargeType] || "unknown";
  const ror = { low: "Likely", "low-med": "Likely", med: "Possible", "med-high": "Less likely", high: "Less likely", unknown: "Depends" }[sev];
  const det = { low: "Rare", "low-med": "Rare", med: "Unlikely", "med-high": "Possible", high: "Possible", unknown: "Depends" }[sev];

  const options = [
    { icon: ShieldCheck, name: "Released on your own recognizance (ROR)", chance: ror, desc: "No money required — you're released on a written promise to return to court. Most common for lower-level charges, first offenses, and strong community ties." },
    { icon: ClipboardCheck, name: "Unsecured / signature bond", chance: "Common option", desc: "You sign for a set amount but only owe it if you fail to appear. No upfront payment." },
    { icon: HandCoins, name: "Cash bail", chance: "Common option", desc: "You pay the full amount to the court; it's returned (minus fees) once the case concludes and you've made all appearances." },
    { icon: Coins, name: "Surety bond (bail bondsman)", chance: "Common option", desc: "A bondsman posts bail for a non-refundable fee (often ~10%). Useful when you can't pay the full cash amount." },
    { icon: ClipboardCheck, name: "Conditional / supervised release", chance: "Possible", desc: "Release with conditions: check-ins, no-contact orders, travel limits, electronic monitoring, or treatment programs." },
    { icon: Lock, name: "Held without bail (detention)", chance: det, desc: "Reserved for the most serious charges or a high flight / safety risk. In federal court this follows a detention hearing." },
  ];

  const factors = [
    "The seriousness of the charge and any mandatory minimums",
    "Your prior criminal record and any past failures to appear",
    "Your ties to the community — job, family, length of residence",
    "Whether you're considered a flight risk or a danger to others",
    "Your ability to pay (courts increasingly must consider this)",
  ];

  const questions = [
    "What is the likely bail amount or release condition for my charge?",
    "Can we argue for release on my own recognizance?",
    "If I can't afford bail, what are my options?",
    "What conditions of release will I have to follow, and for how long?",
  ];

  return { sev, options, factors, questions, primary: PRIMARY[sev], stateNote: stateObj?.bail };
}

export default function Bail() {
  const navigate = useNavigate();
  const [chargeType, setChargeType] = useState("");
  const [stateName, setStateName] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const stateObj = STATES.find((s) => s.name === stateName);
  const result = useMemo(() => (submitted ? buildBail(chargeType, stateObj) : null), [submitted, chargeType, stateObj]);

  return (
    <div className="grain-bg min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="text-center">
          <span className="grid place-items-center h-14 w-14 rounded-2xl bg-forest-100 text-forest-800 mx-auto"><Coins className="h-7 w-7" /></span>
          <span className="mt-4 block font-mono text-xs uppercase tracking-[0.18em] text-terracotta">Guided Bail Estimator</span>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-semibold text-forest-900">Understand your likely bail &amp; release options</h1>
          <p className="mt-3 text-ink-700">Tell us the charge and where it's being handled. We'll explain the release options a judge typically considers. This is general information — the judge decides.</p>
        </div>

        <div className="mt-10 rounded-3xl border border-parchment-200 bg-white p-8">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-ink-700 mb-1.5 block">Type of charge</Label>
              <Select value={chargeType} onValueChange={setChargeType}>
                <SelectTrigger data-testid="bail-charge-select" className="rounded-xl border-parchment-200 h-11"><SelectValue placeholder="Select the closest charge" /></SelectTrigger>
                <SelectContent>{CHARGE_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-ink-700 mb-1.5 block flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Jurisdiction</Label>
              <Select value={stateName} onValueChange={setStateName}>
                <SelectTrigger data-testid="bail-state-select" className="rounded-xl border-parchment-200 h-11"><SelectValue placeholder="Select state or federal" /></SelectTrigger>
                <SelectContent className="max-h-72">{STATES.map((s) => <SelectItem key={s.abbr} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={() => setSubmitted(true)} data-testid="bail-estimate-btn" disabled={!chargeType || !stateName}
            className="mt-6 w-full rounded-xl h-12 bg-forest-800 hover:bg-forest-900 text-parchment-50 gap-2">
            Show likely options <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        {result && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            data-testid="bail-result" className="mt-6 space-y-5">
            <div className="rounded-3xl bg-forest-900 text-parchment-50 p-7">
              <span className="font-mono text-xs uppercase tracking-wider text-sage-500">What's most likely for you</span>
              <p className="mt-2 font-serif text-2xl font-semibold leading-snug">{result.primary}</p>
              {result.stateNote && (
                <p className="mt-3 text-sm text-parchment-200/80"><strong>{stateName}:</strong> {result.stateNote}</p>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {result.options.map((o) => (
                <div key={o.name} className="rounded-2xl border border-parchment-200 bg-white p-5">
                  <div className="flex items-start justify-between gap-2">
                    <span className="grid place-items-center h-10 w-10 rounded-xl bg-forest-100 text-forest-800 shrink-0"><o.icon className="h-5 w-5" /></span>
                    <span className={`text-[10px] font-semibold rounded-full px-2.5 py-1 ${chancePill(o.chance)}`}>{o.chance}</span>
                  </div>
                  <h3 className="mt-3 font-semibold text-forest-900 leading-snug">{o.name}</h3>
                  <p className="mt-1.5 text-sm text-ink-700 leading-relaxed">{o.desc}</p>
                </div>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div className="rounded-2xl border border-parchment-200 bg-white p-6">
                <div className="flex items-center gap-2 text-forest-800 mb-3"><Scale className="h-5 w-5" /><h3 className="font-semibold">What the judge weighs</h3></div>
                <ul className="space-y-2">{result.factors.map((f, i) => <li key={i} className="text-sm text-ink-700 flex gap-2"><span className="text-terracotta">•</span>{f}</li>)}</ul>
              </div>
              <div className="rounded-2xl border border-parchment-200 bg-white p-6">
                <div className="flex items-center gap-2 text-forest-800 mb-3"><MessageCircleQuestion className="h-5 w-5" /><h3 className="font-semibold">Ask your lawyer</h3></div>
                <ul className="space-y-2">{result.questions.map((q, i) => <li key={i} className="text-sm text-ink-700 flex gap-2"><span className="text-terracotta">•</span>{q}</li>)}</ul>
              </div>
            </div>

            <div className="flex items-start gap-2 text-xs text-ink-400 px-1">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>General information only, not legal advice or a prediction. Bail practices vary by county and judge, and many places have reformed cash bail. Your attorney can argue for the best possible release terms.</span>
            </div>

            <div className="rounded-3xl bg-parchment-100 border border-parchment-200 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <p className="text-ink-700">Want a full plan for your situation?</p>
              <Button onClick={() => navigate("/diagnostic")} className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white gap-2">Run the case diagnostic <ArrowRight className="h-4 w-4" /></Button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
